import { Fragment, ReactElement, useRef } from 'react';
import { useRouter } from 'next/router';

import { icon_chevron_right } from '@gn-audio/icons';
import { getTitle } from '@gn-audio/jss-core';

import { useBreakpoint } from '../../../utils';
import { JabraIcon } from '../../JabraIcon/JabraIcon';
import { MobileSearchMenu } from '../../Search';
import { MinorLinksCategoryItem } from '../common';
import { MenuItems } from '../enums/menu-items.enum';
import { menuComponentsMap, submenuComponentsMap } from '../headerComponentsMapper';
import { DISPLAY_VERSIONS } from '../MegaMenuConstants';
import { SubmenuId, SubmenuItem } from '../megaMenuTypes';
import { useHeaderNavigation } from '../providers';
import { MobileMajorLinksCategory } from './MobileMajorLinksCategory';

export const MobileSubmenu = (): ReactElement | null => {
  const { isMobileMenuOpen, menuItems, onMenuItemClick, activeSubmenuItem, activeItemId, route } =
    useHeaderNavigation();
  const router = useRouter();
  const path = router.asPath;

  const majorLinksCategories = activeSubmenuItem?.MajorLinksCategories || [];

  const minorLinksCategories = activeSubmenuItem?.MinorLinksCategories || [];

  const { isBelowLg } = useBreakpoint('lg', true);

  const ref = useRef<HTMLButtonElement>(null);

  const sideBarItems = [];
  if (route?.placeholders?.['search']) {
    sideBarItems.push({ id: 'search', name: MenuItems.Search });
  }
  if (route?.placeholders?.['language-selector']) {
    sideBarItems.push({ id: 'language-selector', name: MenuItems.LanguageSelector });
  }

  const topMenuItems =
    isMobileMenuOpen && !activeSubmenuItem ? (
      <>
        {menuItems.map((menuItem, index) => {
          const hasLink = menuItem.Link?.value?.href;

          return (
            <li
              onClick={() => !hasLink && onMenuItemClick?.(menuItem)}
              className={`box-border ${!hasLink ? 'cursor-pointer' : ''} px-5 py-2 font-semibold leading-6 text-black`}
              key={`${index}-${menuItem.name}`}
            >
              {hasLink ? (
                <a
                  href={menuItem.Link?.value?.href}
                  className={`flex w-full flex-1 items-center justify-between ${DISPLAY_VERSIONS.MOBILE}`}
                >
                  <span className="flex flex-1 text-left">{getTitle(menuItem)}</span>
                </a>
              ) : (
                <button
                  className={`flex w-full flex-1 items-center justify-between ${DISPLAY_VERSIONS.MOBILE}`}
                  ref={index === 0 ? ref : null}
                >
                  <span className="flex flex-1 text-left">{getTitle(menuItem)}</span>
                  <JabraIcon className={`ml-1 h-3 text-accent`} Icon={icon_chevron_right} />
                </button>
              )}
            </li>
          );
        })}
        <li>
          <ul>
            {sideBarItems.map((menuItem, index) => {
              if (menuItem.name === MenuItems.Search && path.includes('search')) {
                return null;
              }
              return (
                <Fragment key={`${index}-${menuItem.name}`}>
                  {menuItem.name === MenuItems.Search ? (
                    <li>
                      <MobileSearchMenu />
                    </li>
                  ) : (
                    <li
                      onClick={() => onMenuItemClick?.(menuItem as SubmenuItem)}
                      className={`mt-3 box-border cursor-pointer bg-gray-50 px-5 py-2 font-semibold leading-6 text-black`}
                    >
                      {menuComponentsMap[menuItem.name as SubmenuId]?.(() =>
                        onMenuItemClick?.(menuItem as SubmenuItem)
                      )}
                    </li>
                  )}
                </Fragment>
              );
            })}
          </ul>
        </li>
      </>
    ) : null;

  return isMobileMenuOpen && isBelowLg ? (
    <div className={`submenu block lg:hidden ${DISPLAY_VERSIONS.MOBILE}`} role="menu">
      <nav className="top-menu">
        <>
          {!activeSubmenuItem && !submenuComponentsMap[activeItemId] ? (
            <ul className="flex flex-col gap-3 pb-4 pt-4" role="menubar">
              {topMenuItems}
            </ul>
          ) : null}
          {activeSubmenuItem ? (
            <>
              <div className="flex-column">
                <div className="flex-1">
                  {majorLinksCategories.map((category, index) => (
                    <MobileMajorLinksCategory key={index} category={category} />
                  ))}
                </div>

                {minorLinksCategories.length > 0 ? (
                  <div className="flex flex-col gap-2 bg-infographicBgGrey1 p-8 pb-7 pl-4 pt-7 lg:pl-8">
                    {minorLinksCategories.map((category, index) => (
                      <MinorLinksCategoryItem
                        key={`${index}-${category.Label?.value}`}
                        category={category}
                        displayVersion={DISPLAY_VERSIONS.MOBILE}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </>
      </nav>
    </div>
  ) : null;
};
